import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
} from "react-icons/ai";

import Overlay from "../../../Componentes/Overlay";
import FormComp from "../../../Componentes/FormComp";
import formStyle from "../../../Componentes/FormComp/FormComp.module.css";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

/* ── Layout base ────────────────────────────────────────────────── */

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const FormSection = styled.div`
    width: 100%;
    text-align: left;

    .${formStyle.formChild} p {
        margin: 15px 0 5px 0;
        padding-left: 2px;
        text-align: left;
    }
`;

const BotaoSalvar = styled.button`
    margin-top: 18px;
    width: 100%;
    height: 42px;
    border: none;
    border-radius: 10px;
    background-color: #4caf50;
    color: #fff;
    font-weight: bold;
    font-size: 13px;
    cursor: pointer;

    &:hover:not(:disabled) { background-color: #45a049; }
    &:disabled { background-color: #c1c1c1; cursor: not-allowed; }
`;

/* ── Seção de anexos ───────────────────────────────────────────── */

const SecaoAnexos = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 16px;
`;

const AnexosLabel = styled.p`
    margin: 0 0 2px 2px;
    font-size: 12px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
`;

const SemAnexos = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    padding: 10px 12px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    text-align: center;
`;

const AnexosCarregando = styled(SemAnexos)` color: #475569; `;

const CardAnexo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background-color: ${(p) => (p.$confirming ? "#fff5f5" : "#f8fafc")};
    border: 1px solid ${(p) => (p.$confirming ? "#fecaca" : "#e2e8f0")};
    border-radius: 10px;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    min-width: 0;
`;

const AnexoIconWrap = styled.div`
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background-color: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const AnexoNome = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AnexoTamanho = styled.span` font-size: 11px; color: #64748b; `;

const AnexoAcoes = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const BotaoAnexo = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) =>
        p.$danger ? "rgba(185,28,28,0.10)" : "rgba(29,78,216,0.08)"};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) =>
            p.$danger ? "rgba(185,28,28,0.20)" : "rgba(29,78,216,0.18)"};
        border-color: ${(p) =>
            p.$danger ? "rgba(185,28,28,0.45)" : "rgba(29,78,216,0.4)"};
    }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ConfirmBox = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
`;

const ConfirmLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #b91c1c;
    white-space: nowrap;
`;

const ConfirmBtn = styled.button`
    all: unset;
    padding: 4px 10px;
    border-radius: 5px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#dc2626")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};
    &:hover { background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#b91c1c")}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BotaoEnviarAnexos = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    background-color: #263b4b;
    color: #fff;

    &:hover:not(:disabled) { background-color: #1e2f3c; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ── Helpers de anexo ──────────────────────────────────────────── */

function formatarTamanho(bytes) {
    if (bytes == null || bytes === "" || Number.isNaN(Number(bytes))) return null;
    const b = Number(bytes);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function nomeAnexo(anexo) {
    if (anexo?.nome_original) return anexo.nome_original;
    const parts = String(anexo?.caminho ?? "").split("/");
    return parts[parts.length - 1] || "Arquivo";
}

function getFileIcon(filename) {
    const name = String(filename ?? "").toLowerCase();
    if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name))
        return <AiOutlineFileImage size={20} color="#4a90e2" />;
    if (name.endsWith(".pdf"))
        return <AiOutlineFilePdf size={20} color="#e63946" />;
    if (/\.(doc|docx)$/i.test(name))
        return <AiOutlineFileWord size={20} color="#2a6db0" />;
    if (/\.(xls|xlsx)$/i.test(name))
        return <AiOutlineFileExcel size={20} color="#207245" />;
    return <AiOutlineFileUnknown size={20} color="#555" />;
}

/* ── Componente ────────────────────────────────────────────────── */

const ModalEditarAtestado = ({ atestado, fechar, onSaved }) => {
    const api = useApi();
    const { notifyError, notifySuccess } = useToast();

    // Campos editáveis
    const [dataAtestado, setDataAtestado] = useState(atestado?.data_atestado ?? "");
    const [diasAbonados, setDiasAbonados] = useState(String(atestado?.dias_abonados ?? "0"));
    const [crm, setCrm] = useState(atestado?.crm ?? "");
    const [nomeMedico, setNomeMedico] = useState(atestado?.nome_medico ?? "");
    const [cid, setCid] = useState(atestado?.cid ?? "");
    const [descricaoCid, setDescricaoCid] = useState(atestado?.descricao_cid ?? "");
    const [localAtestado, setLocalAtestado] = useState(atestado?.local_atestado ?? "");

    const [saving, setSaving] = useState(false);

    // Anexos
    const [anexos, setAnexos] = useState([]);
    const [loadingAnexos, setLoadingAnexos] = useState(true);
    const [confirmDeleteAnexoId, setConfirmDeleteAnexoId] = useState(null);
    const [deletingAnexoId, setDeletingAnexoId] = useState(null);

    const [arquivosPendentes, setArquivosPendentes] = useState([]);
    const [enviandoAnexos, setEnviandoAnexos] = useState(false);

    const formRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            if (!atestado?.id) { setLoadingAnexos(false); return; }
            setLoadingAnexos(true);
            try {
                const { data } = await api.get("/Painel/API/AtendimentoAmb/Atestados/Get/index.php", {
                    params: { id: atestado.id },
                });
                const lista = Array.isArray(data?.data?.anexos) ? data.data.anexos : [];
                setAnexos(lista);
            } catch (err) {
                console.error(err);
                setAnexos([]);
            } finally {
                setLoadingAnexos(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atestado?.id]);

    const handleDeleteAnexo = async (anexoId) => {
        setDeletingAnexoId(anexoId);
        try {
            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/Atestados/DeleteAnexo/index.php",
                { id: anexoId }
            );
            if (data?.success) {
                notifySuccess(data?.message || "Anexo removido.");
                setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
                setConfirmDeleteAnexoId(null);
            } else {
                notifyError(data?.message || "Não foi possível remover o anexo.");
            }
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao remover anexo.");
        } finally {
            setDeletingAnexoId(null);
        }
    };

    const handleEnviarAnexos = async () => {
        if (arquivosPendentes.length === 0 || enviandoAnexos) return;
        setEnviandoAnexos(true);
        try {
            const formData = new FormData();
            formData.append("id", atestado.id);
            arquivosPendentes.forEach((item) => formData.append("files[]", item.file));

            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/Atestados/AddAnexo/index.php",
                formData
            );

            if (data?.success) {
                notifySuccess(data?.message || "Anexos enviados.");
                const novos = Array.isArray(data?.inseridos) ? data.inseridos : [];
                setAnexos((prev) => [...prev, ...novos]);
                setArquivosPendentes([]);
            } else {
                notifyError(data?.message || "Erro ao enviar anexos.");
            }
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao enviar anexos.");
        } finally {
            setEnviandoAnexos(false);
        }
    };

    const validateForm = useCallback(() => {
        const form = formRef.current;
        if (form && !form.checkValidity()) {
            form.reportValidity();
            return false;
        }
        return true;
    }, []);

    const handleSalvar = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!validateForm()) return;

        setSaving(true);
        const formData = new FormData();
        formData.append("id", atestado.id);
        formData.append("data_atestado", dataAtestado);
        formData.append("dias_abonados", String(parseInt(diasAbonados || "0", 10)));
        formData.append("crm", crm);
        formData.append("nome_medico", nomeMedico);
        formData.append("cid", cid);
        formData.append("descricao_cid", descricaoCid);
        formData.append("local_atestado", localAtestado);

        try {
            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/Atestados/Update/index.php",
                formData
            );
            if (data?.success) {
                notifySuccess(data?.message || "Atestado atualizado.");
                onSaved?.();
                return;
            }
            notifyError(data?.message || "Erro ao atualizar atestado.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao atualizar atestado.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state>
            <Modal header close={fechar} size="md">
                <ModalTitle>EDITAR ATESTADO #{atestado?.id}</ModalTitle>

                <FormSection>
                    <Formulario ref={formRef} onSubmit={handleSalvar}>
                        {/* Dados do colaborador — somente leitura */}
                        <FormComp title="CPF" type="text" valor={formatCPF(atestado?.cpf ?? "")} readOnly />
                        <FormComp title="NOME" type="text" valor={atestado?.nome} readOnly />
                        <FormComp title="FUNÇÃO" type="text" valor={atestado?.funcao} readOnly />
                        <FormComp title="CENTRO DE CUSTO" type="text" valor={atestado?.centro_custo} readOnly />

                        {/* Editáveis */}
                        <FormComp
                            title="DATA DO ATESTADO"
                            type="date"
                            valor={dataAtestado}
                            changeFunc={(e) => setDataAtestado(e.target.value)}
                            required
                        />
                        <FormComp
                            title="DIAS ABONADOS"
                            type="number"
                            min="0"
                            valor={diasAbonados}
                            changeFunc={(e) => setDiasAbonados(e.target.value)}
                            required
                        />
                        <FormComp title="CRM" type="text" valor={crm} changeFunc={(e) => setCrm(e.target.value)} />
                        <FormComp
                            title="NOME DO MÉDICO"
                            type="text"
                            valor={nomeMedico}
                            changeFunc={(e) => setNomeMedico(e.target.value)}
                        />
                        <FormComp title="CID" type="text" valor={cid} changeFunc={(e) => setCid(e.target.value)} />
                        <FormComp
                            title="DESCRIÇÃO CID"
                            type="text"
                            valor={descricaoCid}
                            changeFunc={(e) => setDescricaoCid(e.target.value)}
                        />
                        <FormComp
                            title="LOCAL DO ATESTADO"
                            type="text"
                            valor={localAtestado}
                            changeFunc={(e) => setLocalAtestado(e.target.value)}
                        />

                        <SecaoAnexos>
                            <AnexosLabel>ANEXOS</AnexosLabel>

                            {loadingAnexos ? (
                                <AnexosCarregando>Carregando anexos…</AnexosCarregando>
                            ) : anexos.length === 0 ? (
                                <SemAnexos>Nenhum anexo.</SemAnexos>
                            ) : (
                                anexos.map((anexo) => {
                                    const nome = nomeAnexo(anexo);
                                    const tam = formatarTamanho(anexo.tamanho);
                                    const isConfirming = confirmDeleteAnexoId === anexo.id;
                                    const isDeleting = deletingAnexoId === anexo.id;
                                    return (
                                        <CardAnexo key={anexo.id ?? anexo.caminho} $confirming={isConfirming}>
                                            <AnexoIconWrap>{getFileIcon(nome)}</AnexoIconWrap>
                                            <AnexoInfo>
                                                <AnexoNome title={nome}>{nome}</AnexoNome>
                                                {tam && <AnexoTamanho>{tam}</AnexoTamanho>}
                                            </AnexoInfo>

                                            {isConfirming ? (
                                                <ConfirmBox>
                                                    <ConfirmLabel>Confirmar?</ConfirmLabel>
                                                    <ConfirmBtn
                                                        type="button" $cancel disabled={isDeleting}
                                                        onClick={() => setConfirmDeleteAnexoId(null)}
                                                    >NÃO</ConfirmBtn>
                                                    <ConfirmBtn
                                                        type="button" disabled={isDeleting}
                                                        onClick={() => handleDeleteAnexo(anexo.id)}
                                                    >{isDeleting ? "…" : "SIM"}</ConfirmBtn>
                                                </ConfirmBox>
                                            ) : (
                                                <AnexoAcoes>
                                                    <BotaoAnexo
                                                        as="a"
                                                        href={anexo.caminho}
                                                        download
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Baixar anexo"
                                                    >
                                                        <FiDownload size={14} />
                                                    </BotaoAnexo>
                                                    <BotaoAnexo
                                                        $danger
                                                        type="button"
                                                        title="Remover anexo"
                                                        onClick={() => setConfirmDeleteAnexoId(anexo.id)}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </BotaoAnexo>
                                                </AnexoAcoes>
                                            )}
                                        </CardAnexo>
                                    );
                                })
                            )}

                            <MultiFileUploader files={arquivosPendentes} setFiles={setArquivosPendentes} />

                            {arquivosPendentes.length > 0 && (
                                <BotaoEnviarAnexos
                                    type="button"
                                    disabled={enviandoAnexos}
                                    onClick={handleEnviarAnexos}
                                >
                                    {enviandoAnexos
                                        ? "ENVIANDO…"
                                        : `ENVIAR ${arquivosPendentes.length} ANEXO${arquivosPendentes.length > 1 ? "S" : ""}`}
                                </BotaoEnviarAnexos>
                            )}
                        </SecaoAnexos>

                        <BotaoSalvar type="submit" disabled={saving}>
                            {saving ? "SALVANDO…" : "SALVAR ALTERAÇÕES"}
                        </BotaoSalvar>
                    </Formulario>
                </FormSection>
            </Modal>
        </Overlay>
    );
};

export default ModalEditarAtestado;
